import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, Clock, Loader2, Tag, CheckCircle2, Sparkles, ChevronDown, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api, { formatApiError } from "../lib/api";
import { formatDate as fmt } from "../lib/date";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import HeroBackdrop from "../components/HeroBackdrop";

const CATS = ["All", "Group Ride", "Driver Training", "Social", "Tutor Workshop"];

const isNewEvent = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created <= 14 * 24 * 60 * 60 * 1000;
};

export default function Events() {
  const { user } = useAuth();
  const { t } = useContent();
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [booking, setBooking] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/events/past").then(({ data }) => setPastEvents(data)).catch(() => {});
  };
  useEffect(load, []);

  const book = async (id) => {
    if (!user) {
      toast.error("Please sign in to book an event.");
      return;
    }
    setBooking(id);
    try {
      const { data } = await api.post(`/events/${id}/book`);
      toast.success(data.status === "booked" ? "Your place is booked! Check your email." : "Event is full — you're on the waiting list.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBooking(null);
    }
  };

  const filtered = cat === "All" ? events : events.filter((e) => e.category === cat);

  return (
    <div data-testid="events-page">
      <section className="relative overflow-hidden bg-[#0F2744] py-16 lg:py-20">
        <HeroBackdrop sectionKey="events" image={t("img.events", "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=85&w=1600")} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">What's on</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold text-white">{t("events.title", "Club events")}</h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">{t("events.subtitle", "Group rides, training days, club nights and workshops. Sign in to book your place in seconds.")}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="events-filters">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                cat === c ? "bg-[#0F2744] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0F2744]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0F2744]" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-500 py-20">No events in this category yet — check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((e) => (
              <div key={e.id} className="rounded-2xl overflow-hidden bg-white border border-slate-200 flex flex-col" data-testid={`event-card-${e.id}`}>
                {!e.restricted && (
                  <div className="h-44 overflow-hidden relative">
                    <img src={e.image_url} alt={e.title} loading="lazy" className="w-full h-full object-cover" />
                    <Badge className="absolute top-3 left-3 bg-white/90 text-[#0F2744] hover:bg-white">{e.category}</Badge>
                    {isNewEvent(e.created_at) && (
                      <Badge className="absolute top-3 right-3 bg-amber-500 text-[#0F2744] hover:bg-amber-500 font-bold shadow-md animate-pulse flex items-center gap-1" data-testid={`event-new-badge-${e.id}`}>
                        <Sparkles className="h-3.5 w-3.5" /> NEW
                      </Badge>
                    )}
                    {e.spots_left <= 0 ? (
                      <Badge className="absolute bottom-3 left-3 bg-red-600 text-white hover:bg-red-600 font-semibold shadow-md" data-testid={`event-status-${e.id}`}>Fully booked</Badge>
                    ) : e.spots_left <= 3 ? (
                      <Badge className="absolute bottom-3 left-3 bg-orange-500 text-white hover:bg-orange-500 font-semibold shadow-md" data-testid={`event-status-${e.id}`}>Few places left</Badge>
                    ) : (
                      <Badge className="absolute bottom-3 left-3 bg-green-600 text-white hover:bg-green-600 font-semibold shadow-md" data-testid={`event-status-${e.id}`}>Places available</Badge>
                    )}
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  {e.restricted && <Badge className="self-start mb-2 bg-slate-100 text-[#0F2744] hover:bg-slate-100">{e.category}</Badge>}
                  <h3 className="font-bold text-lg text-[#0F2744] leading-snug">{e.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3 flex-1">{e.description}</p>
                  <div className="mt-4 space-y-2 text-sm text-slate-500">
                    <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-amber-500" /> {fmt(e.date)}{!e.restricted && e.time && ` · ${e.time}`}</div>
                    {!e.restricted && (
                      <>
                        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-500" /> {e.location}</div>
                        <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-amber-500" /> {e.price}</div>
                        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-amber-500" /> {e.spots_left > 0 ? `${e.spots_left} places left` : "Full — waiting list"}</div>
                      </>
                    )}
                  </div>
                  {e.restricted ? (
                    <Button asChild className="mt-5 bg-[#0F2744] hover:bg-[#0B192C] text-white font-semibold" data-testid={`event-signin-${e.id}`}>
                      <Link to="/login">Sign in for full details &amp; booking</Link>
                    </Button>
                  ) : (
                    <Button
                      onClick={() => book(e.id)}
                      disabled={booking === e.id}
                      data-testid={`book-btn-${e.id}`}
                      className="mt-5 bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold"
                    >
                      {booking === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : e.spots_left > 0 ? "Book my place" : "Join waiting list"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!user && (
          <div className="mt-10 rounded-2xl bg-blue-50 border border-blue-200 p-6 flex items-start gap-3" data-testid="events-login-notice">
            <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
            <p className="text-sm text-slate-700">
              <Link to="/login" className="font-semibold text-blue-700 underline">Sign in</Link> or <Link to="/join" className="font-semibold text-blue-700 underline">join the club</Link> to book onto events.
            </p>
          </div>
        )}

        {pastEvents.length > 0 && (
          <div className="mt-14 border-t border-slate-200 pt-10" data-testid="past-events-archive">
            <button
              type="button"
              onClick={() => setShowPast((v) => !v)}
              aria-expanded={showPast}
              data-testid="past-events-toggle"
              className="w-full flex items-center justify-between gap-3 text-left group"
            >
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-xl bg-[#0F2744] text-amber-400 flex items-center justify-center shrink-0"><History className="h-5 w-5" /></span>
                <div>
                  <h2 className="text-xl font-bold text-[#0F2744]">Past events archive</h2>
                  <p className="text-sm text-slate-500">{pastEvents.length} event{pastEvents.length === 1 ? "" : "s"} from our history — a look back at what we've done.</p>
                </div>
              </div>
              <ChevronDown className={`h-6 w-6 text-slate-400 transition-transform shrink-0 ${showPast ? "rotate-180" : ""}`} />
            </button>

            {showPast && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="past-events-grid">
                {pastEvents.map((e) => (
                  <div key={e.id} className="rounded-xl overflow-hidden bg-white border border-slate-200 opacity-90 hover:opacity-100 transition-opacity" data-testid={`past-event-${e.id}`}>
                    {!e.restricted && (
                      <div className="h-32 overflow-hidden relative bg-slate-100">
                        {e.image_url && <img src={e.image_url} alt={e.title} loading="lazy" className="w-full h-full object-cover grayscale-[20%]" />}
                        <Badge className="absolute top-2 left-2 bg-white/90 text-[#0F2744] hover:bg-white text-[10px]">{e.category}</Badge>
                      </div>
                    )}
                    <div className="p-3">
                      {e.restricted && <Badge className="mb-1.5 bg-slate-100 text-[#0F2744] hover:bg-slate-100 text-[10px]">{e.category}</Badge>}
                      <h3 className="font-semibold text-sm text-[#0F2744] leading-snug line-clamp-2">{e.title}</h3>
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500"><Calendar className="h-3.5 w-3.5 text-slate-400" /> {fmt(e.date)}</div>
                      {!e.restricted && e.location && <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5 text-slate-400" /> <span className="truncate">{e.location}</span></div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
